import React from 'react';
import { Button, Paper, Typography, Divider, Box } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';
import jsPDF from 'jspdf';
import './PaymentSummary.css';

function PaymentSummary({ 
  selectedInstallment, 
  totalFee, 
  selectedUser, 
  transactionId, 
  paymentMode, 
  paymentTime,
  onBackToHome 
}) {
  const downloadReceipt = () => {
    const doc = new jsPDF();

    // Add logo/header
    doc.setFontSize(20);
    doc.setTextColor(41, 128, 185);
    doc.text('EGurukul Payment Receipt', 105, 20, { align: 'center' });

    // Add border
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, 190, 277);

    // Add content
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    // Receipt details
    doc.text('Payment Receipt', 20, 40);
    doc.setFontSize(10);
    doc.text(`Date: ${paymentTime ? new Date(paymentTime).toLocaleDateString() : new Date().toLocaleDateString()}`, 20, 50);
    doc.text(`Receipt No: ${transactionId?.substring(0, 8) || 'N/A'}`, 20, 55);

    // Divider
    doc.line(20, 60, 190, 60);

    // Student details
    doc.setFontSize(12);
    doc.text('Student Details', 20, 70);
    doc.setFontSize(10);
    doc.text(`Name: ${selectedUser?.user?.firstName || 'N/A'} ${selectedUser?.user?.lastName || 'N/A'}`, 20, 80);
    doc.text(`Phone: ${selectedUser?.user?.phoneNumber || 'N/A'}`, 20, 85);
    doc.text(`Email: ${selectedUser?.user?.email || 'N/A'}`, 20, 90);
    doc.text(`Address: ${selectedUser?.user?.addressDetails?.[0]?.city || 'N/A'}`, 20, 95);

    // Divider
    doc.line(20, 100, 190, 100);

    // Payment details
    doc.setFontSize(12);
    doc.text('Payment Details', 20, 110);
    doc.setFontSize(10);
    doc.text(`Installment ID: ${selectedInstallment?.installmentId || 'N/A'}`, 20, 120);
    doc.text(`Amount Paid: ${totalFee?.toLocaleString() || 'N/A'}`, 20, 125);
    doc.text(`Payment Mode: ${paymentMode || 'Online'}`, 20, 130);
    doc.text(`Academic Year: ${selectedInstallment?.academicYear || 'N/A'}`, 20, 135);

    // Initialize yPosition for fee breakdown
    let yPosition = 145;

    // Fee breakdown - using the same structure as FeeDetails
    if (selectedInstallment?.feeBreakdown && typeof selectedInstallment.feeBreakdown === 'object') {
      doc.text('Fee Breakdown:', 20, yPosition);
      yPosition += 5;
      Object.entries(selectedInstallment.feeBreakdown).forEach(([key, amount]) => {
        if (key !== 'Total Fee' && amount > 0) { // Skip Total Fee and zero amounts
          doc.text(`${key}: ${amount}`, 25, yPosition);
          yPosition += 5;
        }
      });
    }

    // Status
    doc.setFontSize(12);
    doc.setTextColor(46, 125, 50);
    doc.text('Payment Status: SUCCESS', 20, yPosition + 10);

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('This is a computer-generated receipt and does not require a signature.', 105, 270, { align: 'center' });
    doc.text('For any queries, please contact support@egurukul.com', 105, 275, { align: 'center' });

    doc.save(`payment_receipt_${transactionId?.substring(0, 8) || selectedInstallment?.installmentId || 'unknown'}.pdf`);
  };

  return (
    <div className="payment-summary-container">
      <Paper elevation={3} className="payment-summary-paper">
        <Box className="payment-success-header">
          <CheckCircleIcon className="success-icon" />
          <Typography variant="h5" className="success-text">
            Payment Successful!
          </Typography>
        </Box>
        
        <Divider />
        
        <Box className="payment-summary-content">
          <Typography variant="h6" className="summary-title">
            Payment Summary
          </Typography>
          
          <Box className="summary-detail-row">
            <Typography className="summary-label">Installment:</Typography>
            <Typography className="summary-value">{selectedInstallment?.description || 'N/A'}</Typography>
          </Box>
          
          <Box className="summary-detail-row">
            <Typography className="summary-label">Amount Paid:</Typography>
            <Typography className="summary-value">₹{totalFee || 'N/A'}</Typography>
          </Box>
          
          <Box className="summary-detail-row">
            <Typography className="summary-label">Transaction ID:</Typography>
            <Typography className="summary-value">{transactionId || 'N/A'}</Typography>
          </Box>
          
          <Box className="summary-detail-row">
            <Typography className="summary-label">Payment Mode:</Typography>
            <Typography className="summary-value">{paymentMode || 'Online'}</Typography>
          </Box>
          
          <Box className="summary-detail-row">
            <Typography className="summary-label">Payment Time:</Typography>
            <Typography className="summary-value">{paymentTime || new Date().toLocaleString()}</Typography>
          </Box>
          
          <Box className="summary-detail-row">
            <Typography className="summary-label">Status:</Typography>
            <Typography className="summary-value success-text">Successful</Typography>
          </Box>
        </Box>
        
        <Box className="payment-summary-actions">
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={downloadReceipt}
            className="download-button"
          >
            Download Receipt
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={onBackToHome}
            className="home-button"
          >
            Back to Home
          </Button>
        </Box>
      </Paper>
    </div>
  );
}

export default PaymentSummary;